import { useRef } from 'react';
import type { PortfolioScope } from '@promanage/core';
import { Icon, type IconName } from '../../components/Icon';
import { sheetItem } from '../../components/railItem';
import { usePortfolioScope } from './scope';

interface Option {
  value: PortfolioScope;
  label: string;
  /** What it actually covers, in the words a property manager would use. */
  detail: string;
  icon: IconName;
}

const OPTIONS: Option[] = [
  {
    value: 'own',
    label: 'My portfolio',
    detail: 'Properties you are named on',
    icon: 'user',
  },
  {
    value: 'agency',
    label: 'Whole agency',
    detail: 'Every property under management',
    icon: 'people',
  },
];

/**
 * What this scope is called, wherever it is named.
 *
 * The account trigger states the current one under your name and the radiogroup below offers
 * both, so the words live here once. A trigger reading "Whole agency" over a menu offering
 * "Everything" is two controls, not one.
 */
export function portfolioScopeLabel(scope: PortfolioScope): string {
  return (OPTIONS.find((option) => option.value === scope) ?? OPTIONS[0]!).label;
}

/**
 * Which portfolio the console is showing, as a two-way radiogroup.
 *
 * IT LIVES IN THE ACCOUNT MENU, under your own name, beside the organisation you are working
 * in — because that is the same kind of statement. "Whose books am I in" and "how much of them
 * am I looking at" are one question asked twice, and answering them in two different corners of
 * the rail is what makes a total look wrong with no obvious place to go and check why.
 *
 * NOT DRAWN AT ALL for somebody who cannot use it, which is most staff in most agencies. Same
 * rule as `canViewTrustPosition` — it hides a control and withholds nothing, because the server
 * narrows the query on arrival regardless of what this component renders.
 *
 * A picker rather than a cycling button, for the reason `ThemeToggle` is one: both options are
 * visible with the current one marked, so what a press will do is legible before it is pressed.
 * Getting this wrong shows an operator someone else's arrears without saying so.
 *
 * SELECTION DOES NOT FOLLOW FOCUS here, unlike the theme picker. Arrowing between two options
 * that each refetch six lists is a pair of wasted round trips and a screen that flickers through
 * a view nobody asked for; theme changes cost nothing and are instantly reversible, this does not.
 *
 * One implementation for the pointer and the phone, because the popover and the More sheet must
 * not disagree about what "my portfolio" means. `touch` only changes the metrics.
 */
export function PortfolioScopeOptions({
  touch = false,
  onSelect,
}: {
  touch?: boolean;
  /**
   * Close whatever is showing this, once a scope has been chosen.
   *
   * The point of the control is the page behind it. A menu that stays open over five lists
   * reloading is a menu you have to dismiss before you can see the thing you just asked for.
   */
  onSelect?: (() => void) | undefined;
}) {
  const { scope, setScope, canToggle } = usePortfolioScope();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  if (!canToggle) return null;

  const move = (from: number, delta: number) => {
    const next = (from + delta + OPTIONS.length) % OPTIONS.length;
    buttons.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label="Portfolio shown" className="flex flex-col">
      {OPTIONS.map((option, index) => {
        const isSelected = scope === option.value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttons.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => {
              setScope(option.value);
              onSelect?.();
            }}
            onKeyDown={(event) => {
              const delta =
                event.key === 'ArrowDown' || event.key === 'ArrowRight'
                  ? 1
                  : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
                    ? -1
                    : 0;
              if (delta === 0) return;
              event.preventDefault();
              move(index, delta);
            }}
            className={
              touch
                ? sheetItem(isSelected)
                : `flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-[0.8125rem] transition-colors duration-[var(--duration-fast)] ${
                    isSelected
                      ? 'bg-accent-soft font-semibold text-accent'
                      : 'text-ink hover:bg-surface-sunk'
                  }`
            }
          >
            <Icon
              name={option.icon}
              size={touch ? 18 : 15}
              className={isSelected ? 'mt-0.5 shrink-0' : 'mt-0.5 shrink-0 text-ink-subtle'}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{option.label}</span>
              {/* What the label actually covers. "My portfolio" is not self-evident on a house
                  two people are named on, and guessing at it is how a figure gets misread. */}
              <span className="block truncate text-[0.6875rem] font-normal text-ink-subtle">
                {option.detail}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
